/**
 * GuestCaseReview — public, read-only case-review page for an outside guest (§6).
 *
 * A firm member invites a guest (e.g. a CPA witness) to review ONE case via a scoped
 * magic-link; this standalone page (no app chrome, no rail, no login) renders the
 * least-privilege case summary from GET /api/v1/guest/{token}/case. Purely informational —
 * NO mutating controls (the token is read-only; the backend upload gate rejects it anyway).
 *
 * Coded by kimi 2.6 to a conductor-verified spec + K3 design brief; conductor-reviewed
 * (public api.get with no bearer; 410/404 error states; added animate-spin).
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Lock, FileText, AlertTriangle, ExternalLink } from 'lucide-react';
import { api } from '../utils/api';
import { FigureCoWordmark } from '../components/brand/FigureCoLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

interface GuestCaseDocument {
  id: string;
  name: string;
  type: string;
}

interface GuestCaseData {
  read_only: true;
  case_name: string;
  client_name: string;
  law_firm_name: string;
  posture: 'first_party' | 'third_party' | null;
  status: string;
  event_type: string;
  event_date: string;
  claim_amount: number | null;
  documents: GuestCaseDocument[];
  document_count: number;
  invited_by_email: string;
  expires_at: string;
}

function formatStatus(status: string): string {
  return status.replace(/_/g, ' ').toUpperCase();
}

function formatPosture(posture: GuestCaseData['posture']): string | null {
  if (posture === 'first_party') return 'FIRST-PARTY';
  if (posture === 'third_party') return 'THIRD-PARTY';
  return null;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function formatCurrency(n: number | null): string {
  if (n === null || n === 0) return '—';
  return `$${n.toLocaleString()}`;
}

// Court-exhibit: humanize raw DB enums ("fire" -> "Fire", "water_damage" -> "Water Damage").
function titleCase(s: string): string {
  if (!s) return s;
  return s
    .replace(/_/g, ' ')
    .split(' ')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');
}

export default function GuestCaseReview() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<GuestCaseData | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorCode, setErrorCode] = useState<'expired' | 'invalid' | 'other' | null>(null);

  useEffect(() => {
    if (!token) {
      setErrorCode('invalid');
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchData = async () => {
      try {
        const result = await api.get<GuestCaseData>(`/api/v1/guest/${token}/case`);
        if (!cancelled) {
          setData(result);
          setErrorCode(null);
        }
      } catch (err: any) {
        if (cancelled) return;
        const status = err?.status ?? err?.response?.status ?? err?.code;
        const message = String(err?.message ?? '');
        if (status === 410 || message.includes('410')) {
          setErrorCode('expired');
        } else if (status === 404 || message.includes('404')) {
          setErrorCode('invalid');
        } else {
          setErrorCode('invalid');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{ background: 'var(--color-bg-base, var(--color-bg-surface))' }}
      >
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--color-text-muted)' }} />
      </div>
    );
  }

  if (errorCode) {
    const isExpired = errorCode === 'expired';
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{ background: 'var(--color-bg-base, var(--color-bg-surface))' }}
      >
        <div
          className="w-full max-w-sm rounded-xl px-6 py-10 text-center space-y-4"
          style={{
            border: '1px solid var(--color-border)',
            background: 'var(--color-bg-surface)',
          }}
        >
          <AlertTriangle
            className="w-6 h-6 mx-auto"
            style={{ color: 'var(--color-text-muted)' }}
          />
          <h2 className="cs-h2" style={{ color: 'var(--color-text-primary)' }}>
            {isExpired ? 'This link has expired' : 'Link not found'}
          </h2>
          <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
            {isExpired
              ? 'Ask your contact at the firm for a new link.'
              : 'This review link is invalid or no longer active.'}
          </p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{ background: 'var(--color-bg-base, var(--color-bg-surface))' }}
      >
        <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
          Unable to load case details.
        </p>
      </div>
    );
  }

  const postureLabel = formatPosture(data.posture);
  const metaLine = [data.law_firm_name, postureLabel].filter(Boolean).join(' · ');

  return (
    <div
      className="min-h-screen px-4 pt-12 pb-16"
      style={{ background: 'var(--color-bg-base, var(--color-bg-surface))' }}
    >
      <div className="mx-auto max-w-[720px] space-y-8">
        {/* Top Bar */}
        <div className="flex items-center justify-between gap-4">
          <FigureCoWordmark size="sm" tagline={false} />
          <span
            className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wide px-2 py-1 rounded"
            style={{
              color: 'var(--color-text-primary)',
              border: '1px solid var(--color-text-muted)',
            }}
          >
            <Lock className="w-3.5 h-3.5" style={{ color: 'var(--color-text-primary)' }} />
            GUEST · READ-ONLY
          </span>
        </div>

        {/* Header */}
        <div className="space-y-2">
          <h1 className="cs-h1" style={{ color: 'var(--color-text-primary)' }}>
            {data.case_name}
          </h1>
          <p
            className="cs-meta font-mono uppercase"
            style={{ color: 'var(--color-text-muted)' }}
          >
            {metaLine}
          </p>
          <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            Shared for review by {data.invited_by_email} · <span style={{ color: 'var(--color-text-muted)' }}>read-only</span>
          </p>
        </div>

        {/* Case Facts */}
        <div
          className="rounded-xl overflow-hidden"
          style={{
            border: '1px solid var(--color-border)',
            background: 'var(--color-bg-surface)',
          }}
        >
          <div className="px-5 py-3 flex items-baseline justify-between gap-4">
            <span
              className="font-mono text-[10px] uppercase tracking-wide"
              style={{ color: 'var(--color-text-muted)' }}
            >
              Status
            </span>
            <span className="text-sm" style={{ color: 'var(--color-text-primary)' }}>
              {formatStatus(data.status)}
            </span>
          </div>
          <div
            className="px-5 py-3 flex items-baseline justify-between gap-4"
            style={{ borderTop: '1px solid var(--color-border)' }}
          >
            <span
              className="font-mono text-[10px] uppercase tracking-wide"
              style={{ color: 'var(--color-text-muted)' }}
            >
              Event
            </span>
            <span className="text-sm text-right" style={{ color: 'var(--color-text-primary)' }}>
              {titleCase(data.event_type)} — {formatDate(data.event_date)}
            </span>
          </div>
          <div
            className="px-5 py-3 flex items-baseline justify-between gap-4"
            style={{ borderTop: '1px solid var(--color-border)' }}
          >
            <span
              className="font-mono text-[10px] uppercase tracking-wide"
              style={{ color: 'var(--color-text-muted)' }}
            >
              Claim amount
            </span>
            <span
              className="font-mono text-sm text-right"
              style={{ color: 'var(--color-text-primary)' }}
            >
              {formatCurrency(data.claim_amount)}
            </span>
          </div>
        </div>

        {/* Documents */}
        <div className="space-y-3">
          <div className="flex items-baseline gap-2">
            <h2 className="cs-h2" style={{ color: 'var(--color-text-primary)' }}>
              Documents
            </h2>
            <span
              className="font-mono text-xs"
              style={{ color: 'var(--color-text-muted)' }}
            >
              ({data.document_count})
            </span>
          </div>
          <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
            Exhibits on file for this matter — open to review (read-only).
          </p>
          <div
            className="rounded-xl overflow-hidden"
            style={{
              border: '1px solid var(--color-border)',
              background: 'var(--color-bg-surface)',
            }}
          >
            {data.documents.length > 0 ? (
              data.documents.map((doc, i) => (
                <div
                  key={`${doc.name}-${i}`}
                  className="flex items-center gap-3 px-5 py-3"
                  style={{
                    borderTop: i > 0 ? '1px solid var(--color-border)' : 'none',
                  }}
                >
                  <FileText
                    className="w-3.5 h-3.5 flex-shrink-0"
                    style={{ color: 'var(--color-text-muted)' }}
                  />
                  <span
                    className="flex-1 text-sm min-w-0 truncate"
                    style={{ color: 'var(--color-text-primary)' }}
                  >
                    {doc.name}
                  </span>
                  {doc.type && (
                    <span
                      className="font-mono text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded flex-shrink-0"
                      style={{
                        color: 'var(--color-text-muted)',
                        border: '1px solid var(--color-border)',
                      }}
                    >
                      {doc.type.replace(/_/g, ' ')}
                    </span>
                  )}
                  <a
                    href={`${BACKEND_URL}/api/v1/guest/${token}/document/${doc.id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wide flex-shrink-0 transition-colors hover:text-[var(--color-text-primary)]"
                    style={{ color: 'var(--color-text-muted)' }}
                  >
                    View
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              ))
            ) : (
              <p
                className="py-12 text-center text-sm"
                style={{ color: 'var(--color-text-muted)' }}
              >
                No documents shared.
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="space-y-1 pt-2" style={{ borderTop: '1px solid var(--color-border)' }}>
          <p className="font-mono text-[10px] uppercase tracking-wide pt-3" style={{ color: 'var(--color-text-muted)' }}>
            Privileged &amp; Confidential — Prepared in anticipation of litigation
          </p>
          <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
            Read-only review access to this case only. Link expires {formatDate(data.expires_at)}.
          </p>
        </div>
      </div>
    </div>
  );
}
